import { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import LoadingScreen from "./utils/LoadingScreen";
import AuthGuard from "./guard/AuthGuard";
import AdminGuard from "./guard/AdminGuard";
import SurveyorGuard from "./guard/SurveyorGuard";
import GuestGuard from "./guard/GuestGuard";
import AuthenticatedLayout from "./components/AuthenticatedLayout/AuthenticatedLayout";
import Report from "./Pavement/components/reports/Reports";
import VersionControl from "./Pavement/components/VersionControl/VersionControl";
import SideBar from "./Pavement/components/sideBar/SideBar";
import Notifications from "./Pavement/components/Notifications/Notifications";
import Calculator from "./Pavement/components/Calculator/Calculator";
import Traffic from "./Pavement/components/Traffic/Traffic";
import ViewTask from "./Pavement/components/ViewTask/ViewTask";
import MyTask from "./Pavement/components/MyTask/MyTask";

const Home = lazy(() => import("./utils/Home/Home"));
const NotFound = lazy(() => import("./authentication/404/NotFound"));
const Unauthorized = lazy(() => import("./authentication/404/Unauthorized"));
const Login = lazy(() => import("./authentication/Login/Login"));
const Signup = lazy(() => import("./authentication/Signup/Signup"));
const Register = lazy(() => import("./authentication/Register/Register"));
const ProfileSetup = lazy(() => import("./authentication/ProfileSetup/ProfileSetup"));
const UserprofileSetup = lazy(() => import("./authentication/UserprofileSetup/UserprofileSetup"));
const Forgot = lazy(() => import("./authentication/Forgot/Forgot"));
const Reset = lazy(() => import("./authentication/Reset/Reset"));
const Map = lazy(() => import("./Pavement/components/Map/Map"));
const ProjectList = lazy(() => import("./Pavement/components/ProductList/ProductList"));
const MultiMap = lazy(() => import("./Pavement/components/MultiMap/MultiMap"));
const NewPassword = lazy(() => import("./authentication/NewPassword/NewPassword"));
const ManageUser = lazy(() => import("./Pavement/components/ManageUser/ManageUser"));
const Profile = lazy(() => import("./utils/Profile/Profile"));
const EditEmail = lazy(() => import("./utils/Profile/EditEmail"));
const SafetyReports = lazy(() => import("./Pavement/components/reports/SafetyReports"));
const Video = lazy(() => import("./Pavement/components/Video/Video"));
const App = lazy(() => import("./Inventory/components/App/App"));
const Inventory_Report = lazy(() => import("./Inventory/components/Report/Report"));
const DownloadReports = lazy(() => import("./Pavement/components/DownloadReports/DownloadReports"));
const Data = lazy(() => import("./DataUpload/Data"));
const DataUploadEntry = lazy(() => import("./DataUpload/DataUploadEntry"));
const ProfileBranding = lazy(() => import("./Pavement/components/ProfileBranding/ProfileBranding"));
const Settings = lazy(() => import("./Pavement/components/Settings/Settings"));
const VideoApp = lazy(() => import("./Video/components/App/App"));
const RoadSurveys = lazy(() => import("./Pavement/components/RoadSurveys/RoadSurveys"));
const RoadSurveyDetail = lazy(() => import("./Pavement/components/RoadSurveys/RoadSurveyDetail"));
const SurveyObservations = lazy(() => import("./Pavement/components/RoadSurveys/SurveyObservations"));
const Tickets = lazy(() => import("./Pavement/components/Tickets/Tickets"));
const PrePostAnalysis = lazy(() => import("./Pavement/components/PrePostAnalysis/PrePostAnalysis"));
const WeeklyComparison = lazy(() => import("./Pavement/components/WeeklyComparison/WeeklyComparison"));
const ViewImage = lazy(() => import("./utils/ViewImage"));

export const renderRoutes = () => (
  <Suspense fallback={<LoadingScreen />}>
    <Routes>
      {/* Public / guest routes */}
      <Route path="/404" element={<NotFound />} />
      <Route path="/resetPassword" element={<NewPassword />} />
      <Route path="/login" element={<GuestGuard><Login /></GuestGuard>} />
      <Route path="/signin" element={<GuestGuard><Login /></GuestGuard>} />
      <Route path="/sign-in" element={<GuestGuard><Login /></GuestGuard>} />
      <Route path="/signup" element={<GuestGuard><Signup /></GuestGuard>} />
      <Route path="/register" element={<GuestGuard><Register /></GuestGuard>} />
      <Route path="/activateprofile" element={<GuestGuard><ProfileSetup /></GuestGuard>} />
      {/* No GuestGuard — invited users must see registration form even if admin is logged in */}
      <Route path="/userprofileSetup" element={<UserprofileSetup />} />
      <Route path="/forgot" element={<GuestGuard><Forgot /></GuestGuard>} />
      {/* Public image-viewer endpoint hit by xlsx defect-report hyperlinks. */}
      <Route path="/view-image" element={<ViewImage />} />
      <Route path="/reset" element={<GuestGuard><Reset /></GuestGuard>} />

      {/* Authenticated routes — single shared layout, sidebar never remounts */}
      <Route
        element={
          <AuthGuard>
            <AuthenticatedLayout />
          </AuthGuard>
        }
      >
        <Route path="/home" element={<Home />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/pavement/app" element={<AdminGuard><ProjectList /></AdminGuard>} />
        <Route path="/pavement/map/:id" element={<AdminGuard><Map /></AdminGuard>} />
        <Route path="/pavement/multiMap" element={<AdminGuard><MultiMap /></AdminGuard>} />
        <Route path="/pavement/report" element={<AdminGuard><Report /></AdminGuard>} />
        <Route path="/pavement/safetyReports" element={<AdminGuard><SafetyReports /></AdminGuard>} />
        <Route path="/pavement/finalReport" element={<AdminGuard><Home /></AdminGuard>} />
        <Route path="/pavement/notifications" element={<AdminGuard><Notifications /></AdminGuard>} />
        <Route path="/pavement/versionControl" element={<AdminGuard><VersionControl /></AdminGuard>} />
        <Route path="/pavement/calculator" element={<AdminGuard><Calculator /></AdminGuard>} />
        <Route path="/pavement/traffic" element={<AdminGuard><Traffic /></AdminGuard>} />
        <Route path="/pavement/video" element={<AdminGuard><Video /></AdminGuard>} />
        <Route path="/pavement/multiReport" element={<AdminGuard><DownloadReports /></AdminGuard>} />
        <Route path="/pavement/viewTask" element={<ViewTask />} />
        <Route path="/pavement/myTask" element={<SurveyorGuard><MyTask /></SurveyorGuard>} />
        <Route path="/pavement/settings" element={<AdminGuard><Settings /></AdminGuard>} />
        <Route path="/pavement/manageUser" element={<AdminGuard><ManageUser /></AdminGuard>} />
        <Route path="/pavement/profileBranding" element={<AdminGuard><ProfileBranding /></AdminGuard>} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/edit-email" element={<EditEmail />} />
        <Route path="/test" element={<SideBar />} />
        <Route path="/Inventory/app" element={<AdminGuard><App /></AdminGuard>} />
        <Route path="/Inventory/report" element={<AdminGuard><Inventory_Report /></AdminGuard>} />
        <Route path="/road/app" element={<DataUploadEntry />} />
        <Route path="/road/dashcam" element={<Data />} />
        <Route path="/road/coordinates" element={<Data />} />
        <Route path="/road/surveys" element={<RoadSurveys />} />
        <Route path="/road/surveys/:road_id" element={<RoadSurveyDetail />} />
        <Route path="/road/surveys/:road_id/observations/:survey_uid" element={<SurveyObservations />} />
        <Route path="/pavement/prePostAnalysis" element={<AdminGuard><PrePostAnalysis /></AdminGuard>} />
        <Route path="/pavement/weeklyComparison" element={<AdminGuard><WeeklyComparison /></AdminGuard>} />
        <Route path="/pavement/tickets" element={<Tickets />} />
        <Route path="/video/app" element={<VideoApp />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<AuthGuard><Login /></AuthGuard>} />
    </Routes>
  </Suspense>
);

export default renderRoutes;
